import {
  ArrowDownIcon,
  BackwardIcon,
  CheckIcon,
  DocumentTextIcon,
  ScaleIcon,
  SparklesIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { useFrame } from '../engine'
import { anim, lerp, pv, seg, smooth } from '../motion'
import { COLORS, Shape } from '../primitives'
import { Envelope, Flight, Heart, Person, Walker } from '../v3/figures'
import { H, U, W, iso, onWallA } from '../v3/iso'
import { Beam, Panel } from '../v3/panels'
import { BEATS, atB, inB } from './beats'
import {
  AIVY_APPEARS,
  OfficeWorld4,
  SILHOUETTES,
  SPOTS,
  STAFF_BY_ID,
  Silhouette,
  TALENTS,
  Talent,
  spawnAt
} from './world'

const range = PropTypes.arrayOf(PropTypes.number)

// ---------- Kamera ----------

const shot = (l, f, z, ox = 0) => ({ l, f, z, ox })
const OVERVIEW = [13, 11, 11]

const SHOTS = [
  shot(0, OVERVIEW, 0.8),
  shot(atB('intro', 0.6), OVERVIEW, 0.8),
  shot(atB('ohne', 0.08), [15, 8, 1.5], 1.2),
  shot(atB('ohne', 0.95), [15, 8, 1.5], 1.2),
  shot(atB('rewind', 0.5), [13, 10, 3], 0.95),
  shot(atB('rewind', 0.9), [13, 10, 3], 0.95),
  shot(atB('anforderung', 0.15), [11, 13, 2], 1.05),
  shot(atB('anforderung', 0.95), [11, 13, 2], 1.05),
  shot(atB('menschen', 0.1), [15, 10, 1], 1),
  shot(atB('menschen', 0.55), [15, 10, 1], 1),
  shot(atB('menschen', 0.75), [12.2, 17.8, 1.4], 1.7),
  shot(atB('menschen', 0.98), [12.2, 17.8, 1.4], 1.7),
  shot(atB('regeln', 0.12), [12.2, 17.8, 1], 1.45, -0.2),
  shot(atB('regeln', 0.92), [12.2, 17.8, 1], 1.45, -0.2),
  shot(atB('shortlist', 0.15), [11.5, 8, 3], 1.25),
  shot(atB('shortlist', 0.92), [11.5, 8, 3], 1.25),
  shot(atB('kreuz', 0.12), [9, 17, 1.2], 1.35),
  shot(atB('kreuz', 0.92), [9, 17, 1.2], 1.35),
  shot(atB('einstellung', 0.1), [12.2, 9.6, 1.5], 1.9),
  shot(atB('einstellung', 0.45), [12.2, 9.6, 1.5], 1.9),
  shot(atB('einstellung', 0.65), [18, 15, 1], 1.4),
  shot(atB('einstellung', 0.95), [18, 15, 1], 1.4),
  shot(atB('fair', 0.1), [17, 9, 1], 1.05),
  shot(atB('fair', 0.92), [17, 9, 1], 1.05),
  shot(atB('finale', 0.25), OVERVIEW, 0.8),
  shot(1, OVERVIEW, 0.8)
]

function camera(l) {
  let i = 1
  while (i < SHOTS.length - 1 && l > SHOTS[i].l) i++
  const a = SHOTS[i - 1]
  const b = SHOTS[i]
  const t = smooth(seg(l, a.l, b.l))
  const pa = iso(...a.f)
  const pb = iso(...b.f)
  return {
    x: lerp(pa.x, pb.x, t),
    y: lerp(pa.y, pb.y, t),
    z: Math.exp(lerp(Math.log(a.z), Math.log(b.z), t)),
    ox: lerp(a.ox, b.ox, t)
  }
}

// ---------- Wege ----------

const rnd = (i, s) => {
  const v = Math.sin(i * 127.1 + s * 311.7) * 43758.5453
  return v - Math.floor(v)
}

const DOOR = iso(SPOTS.door.x, SPOTS.door.y, 2.2)
const MACHINE_TOP = iso(5, 4, 3.4)
const head = (p) => {
  const s = iso(p.x, p.y)
  return { x: s.x, y: s.y - 52 }
}

// Auf dem Weg zur Tür: durch den Gang zwischen HR und Customer Success
const exitPoints = (from) => [
  from,
  { x: 16.2, y: 13.2 },
  { x: 16.4, y: 6.2 },
  { x: 22.2, y: 5.4 },
  SPOTS.door
]

const timed = (points, start, step) =>
  points.map((p, i) => ({ at: start + i * step, x: p.x, y: p.y }))

const LEAVERS = TALENTS.filter((t) => t.id !== 'lena' && t.id !== 'jonas')

function talentPath(t) {
  const i = TALENTS.indexOf(t)
  const home = { x: t.x, y: t.y }
  const start = [{ at: spawnAt(i)[0], ...home }]
  if (t.id === 'lena') {
    return [
      ...start,
      { at: atB('shortlist', 0.15), ...home },
      { at: atB('shortlist', 0.45), ...SPOTS.front[0] },
      { at: atB('einstellung', 0.05), ...SPOTS.front[0] },
      { at: atB('einstellung', 0.2), ...SPOTS.interview },
      { at: atB('einstellung', 0.55), ...SPOTS.interview },
      { at: atB('einstellung', 0.64), x: 14.8, y: 13.8 },
      { at: atB('einstellung', 0.72), x: 16.6, y: 19.4 },
      { at: atB('einstellung', 0.8), x: 20.4, y: 18.4 },
      { at: atB('einstellung', 0.86), ...SPOTS.csSeat }
    ]
  }
  if (t.id === 'jonas') {
    return [
      ...start,
      { at: atB('kreuz', 0.15), ...home },
      { at: atB('kreuz', 0.32), x: 8.2, y: 14.2 },
      { at: atB('kreuz', 0.48), x: 6.4, y: 19.6 },
      { at: atB('kreuz', 0.6), ...SPOTS.vSeat }
    ]
  }
  const k = LEAVERS.indexOf(t)
  const exitStart = atB('fair', 0.14 + k * 0.05)
  const step = atB('fair', 0.035) - atB('fair', 0)
  const pos = t.front != null ? SPOTS.front[t.front] : home
  const shortlisted =
    t.front != null
      ? [
          { at: atB('shortlist', 0.2 + t.front * 0.05), ...home },
          { at: atB('shortlist', 0.5 + t.front * 0.05), ...pos }
        ]
      : []
  return [...start, ...shortlisted, ...timed(exitPoints(pos), exitStart, step)]
}

const exitArrival = (path) => {
  const end = path[path.length - 1].at
  return [end - 0.004, end]
}

// ---------- Fliegende Dinge ----------

const PAST_LETTERS = Array.from({ length: 14 }, (_, i) => {
  const desk = iso(10.2 + rnd(i, 1) * 1.6, 7.9 + rnd(i, 2) * 0.6, 1)
  const to = { x: desk.x, y: desk.y - 4 - i * 2.4 }
  return {
    rot: rnd(i, 3) * 30 - 15,
    legs: [
      {
        from: DOOR,
        to,
        at: inB('ohne', 0.04 + i * 0.018, 0.14 + i * 0.018),
        arc: 120 + rnd(i, 4) * 60,
        spin: rnd(i, 5) * 200 - 100
      },
      {
        from: to,
        to: DOOR,
        at: inB('rewind', 0.15 + i * 0.02, 0.45 + i * 0.02),
        arc: 120
      }
    ]
  }
})

const LETTERS = TALENTS.map((t, i) => {
  const land = iso(t.x, t.y)
  return {
    rot: rnd(i, 7) * 40 - 20,
    legs: [
      {
        from: DOOR,
        to: { x: land.x, y: land.y - 20 },
        at: [spawnAt(i)[0] - 0.012, spawnAt(i)[0] + 0.004],
        arc: 140,
        spin: 180
      }
    ]
  }
})

const EXPERTS = ['csLead', 'csMate', 'vLead']
const MARA = head(SPOTS.mara)

const INVITES = EXPERTS.map((id, i) => ({
  legs: [
    {
      from: MARA,
      to: head(STAFF_BY_ID[id]),
      at: inB('anforderung', 0.04 + i * 0.04, 0.16 + i * 0.04),
      arc: 70,
      spin: 20
    }
  ]
}))

const ANALYSES = EXPERTS.map((id, i) => ({
  kind: ['dot', 'tri', 'sq'][i],
  color: ['pink', 'yellow', 'green'][i],
  legs: [
    {
      from: head(STAFF_BY_ID[id]),
      to: MACHINE_TOP,
      at: inB('anforderung', 0.3 + i * 0.04, 0.44 + i * 0.04),
      arc: 90,
      spin: 180
    }
  ]
}))

const renderShape = (it) => (
  <g transform='translate(-9 -9)'>
    <Shape kind={it.kind} color={it.color} size={18} />
  </g>
)

// ---------- Overlays in Weltkoordinaten ----------

function Anchor({ at3, dy = 0, children }) {
  const p = iso(...at3)
  return (
    <div className='off-anchor' style={{ left: p.x, top: p.y - dy }}>
      {children}
    </div>
  )
}

Anchor.propTypes = {
  at3: range.isRequired,
  dy: PropTypes.number,
  children: PropTypes.node
}

function Bubble({ at3, dx = 0, dy = 84, show, hide, tone, children }) {
  return (
    <Anchor at3={at3} dy={dy}>
      <div
        className={clsx('off-bubble', tone && `off-bubble--${tone}`)}
        style={{
          marginLeft: dx,
          ...anim({ at: show, out: hide, y: 14, oy: -10, s: 0.7 })
        }}
      >
        {children}
      </div>
    </Anchor>
  )
}

Bubble.propTypes = {
  at3: range.isRequired,
  dx: PropTypes.number,
  dy: PropTypes.number,
  show: range.isRequired,
  hide: range,
  tone: PropTypes.string,
  children: PropTypes.node
}

const SHORTLIST = [
  { name: 'Lena K.', value: 91, color: 'pink' },
  { name: 'Aylin S.', value: 86, color: 'yellow' },
  { name: 'Mo A.', value: 82, color: 'green' }
]

function Board() {
  return (
    <div
      className='off-board'
      style={{
        width: 7 * U,
        height: 3.4 * U,
        transform: onWallA(iso(8.5, 0, 5.7))
      }}
    >
      <div
        className='off-board__idle'
        style={anim({
          at: [-1, 0],
          out: inB('shortlist', 0.24, 0.3),
          y: 0,
          oy: 0
        })}
      >
        <span>Wir suchen</span>
        <strong>Customer Success Manager:in</strong>
      </div>
      <div
        className='off-board__list'
        style={anim({ at: inB('shortlist', 0.3, 0.38), y: 0 })}
      >
        <div className='off-board__head'>Shortlist · Customer Success</div>
        {SHORTLIST.map((s, i) => (
          <div
            key={s.name}
            className={clsx('off-board__row', i === 0 && 'is-top')}
            style={anim({
              at: inB('shortlist', 0.36 + i * 0.06, 0.44 + i * 0.06),
              x: -20,
              y: 0
            })}
          >
            <i style={{ background: COLORS[s.color] }} />
            <span>{s.name}</span>
            <b>{s.value}&nbsp;%</b>
          </div>
        ))}
      </div>
    </div>
  )
}

const PROFILE = [
  { name: 'Emotionswahrnehmung', ideal: [62, 86], trans: [52, 94] },
  { name: 'Teamarbeit', ideal: [14, 40], trans: [6, 52] },
  { name: 'Gewissenhaftigkeit', ideal: [50, 78], trans: [40, 88] }
]

function Overlays() {
  const mara = [SPOTS.mara.x, SPOTS.mara.y, 0]
  const staff = (id) => [STAFF_BY_ID[id].x, STAFF_BY_ID[id].y, 0]
  const lenaAtInterview = [SPOTS.interview.x, SPOTS.interview.y, 0]

  return (
    <div className='off-overlay'>
      <div
        className='off-machine-label'
        style={{
          width: 3 * U,
          transform: onWallA(iso(3.5, 5.5, 2.1)),
          ...AIVY_APPEARS
        }}
      >
        aivy
      </div>
      <Board />

      {/* Freie Plätze */}
      <Anchor at3={[SPOTS.csSeat.x, SPOTS.csSeat.y, 0]} dy={40}>
        <div
          className='off-seat'
          style={anim({
            at: inB('ohne', 0.02, 0.06),
            out: inB('ohne', 0.54, 0.6),
            s: 0.3,
            y: 10
          })}
        >
          ?
        </div>
      </Anchor>
      <Anchor at3={[SPOTS.csSeat.x, SPOTS.csSeat.y, 0]} dy={40}>
        <div
          className='off-seat'
          style={anim({
            at: inB('ohne', 0.92, 0.97),
            out: inB('anforderung', 0.55, 0.62),
            s: 0.3,
            y: 10
          })}
        >
          ?
        </div>
      </Anchor>
      <Anchor at3={[SPOTS.csSeat.x, SPOTS.csSeat.y, 0]} dy={92}>
        <Silhouette
          shapes={SILHOUETTES.cs}
          label='Customer Success'
          fillAt={inB('einstellung', 0.86, 0.92)}
        />
      </Anchor>
      <Anchor at3={[SPOTS.vSeat.x, SPOTS.vSeat.y, 0]} dy={92}>
        <Silhouette
          shapes={SILHOUETTES.v}
          label='Vertrieb'
          fillAt={inB('kreuz', 0.6, 0.66)}
        />
      </Anchor>

      {/* Ohne Aivy */}
      <Bubble
        at3={mara}
        show={inB('ohne', 0.34, 0.4)}
        hide={inB('ohne', 0.5, 0.55)}
        tone='question'
      >
        <small>Viel Arbeit, wenig Einblick</small>
        Ich lese jeden Lebenslauf. Aber wer passt wirklich ins Team?
      </Bubble>
      <Bubble
        at3={[SPOTS.csSeat.x, SPOTS.csSeat.y, 0]}
        dx={60}
        show={inB('ohne', 0.68, 0.73)}
        hide={inB('ohne', 0.8, 0.85)}
      >
        Hm, das ist irgendwie nicht meins.
      </Bubble>
      <Bubble
        at3={staff('csLead')}
        show={inB('ohne', 0.9, 0.95)}
        hide={inB('rewind', 0.1, 0.2)}
      >
        Schon wieder jemand weg?
      </Bubble>

      {/* Anforderungen */}
      {EXPERTS.map((id, i) => (
        <Anchor key={id} at3={staff(id)} dy={84}>
          <div
            className='off-tag off-tag--green'
            style={anim({
              at: inB('anforderung', 0.16 + i * 0.05, 0.24 + i * 0.05),
              out: inB('anforderung', 0.5, 0.58),
              y: 10,
              s: 0.5
            })}
          >
            <CheckIcon width={12} height={12} /> Anforderungsanalyse
          </div>
        </Anchor>
      ))}
      <Anchor at3={[5, 4, 3.4]} dy={48}>
        <div
          className='hum-profile'
          style={anim({
            at: inB('anforderung', 0.42, 0.52),
            out: inB('anforderung', 0.9, 1),
            y: 20,
            s: 0.6
          })}
        >
          <div className='off-analysis__head'>Anforderungsprofil</div>
          {PROFILE.map((d) => (
            <div key={d.name} className='hum-profile__row'>
              <span>{d.name}</span>
              <Beam ideal={d.ideal} trans={d.trans} />
            </div>
          ))}
        </div>
      </Anchor>

      {/* Shortlist */}
      <Anchor at3={mara} dy={86}>
        <div
          className='off-tag off-tag--blue'
          style={anim({
            at: inB('shortlist', 0.6, 0.68),
            out: inB('shortlist', 0.94, 1),
            y: 10,
            s: 0.5
          })}
        >
          <DocumentTextIcon width={13} height={13} /> Passungsprofil als PDF für
          alle drei
        </div>
      </Anchor>

      {/* Kreuzmatching */}
      <Bubble
        at3={[14.8, 17.6, 0]}
        show={inB('kreuz', 0.04, 0.12)}
        hide={inB('kreuz', 0.22, 0.28)}
        tone='blue'
      >
        Kreuzmatching: Jonas passt in den Vertrieb.
      </Bubble>
      <Bubble
        at3={staff('vLead')}
        show={inB('kreuz', 0.66, 0.74)}
        hide={inB('kreuz', 0.92, 1)}
        tone='green'
      >
        Willkommen, Jonas!
      </Bubble>

      {/* Einstellung */}
      <Anchor at3={lenaAtInterview} dy={92}>
        <div
          className='off-interview'
          style={anim({
            at: inB('einstellung', 0.2, 0.28),
            out: inB('einstellung', 0.48, 0.54),
            y: 20,
            s: 0.7
          })}
        >
          <div className='off-interview__head'>
            Interviewfragen aus dem Passungsprofil
          </div>
          <ol>
            <li>
              Erzähl von einem Kunden, der unzufrieden war. Was hast du getan?
            </li>
            <li>Wann arbeitest du lieber allein, wann im Team?</li>
          </ol>
        </div>
      </Anchor>
      <Bubble
        at3={staff('csLead')}
        show={inB('einstellung', 0.9, 0.96)}
        hide={inB('fair', 0.06, 0.12)}
        tone='green'
      >
        Willkommen im Team, Lena!
      </Bubble>

      {/* Fair für alle */}
      <Bubble
        at3={[SPOTS.door.x + 0.6, SPOTS.door.y, 4.6]}
        dx={80}
        dy={0}
        show={inB('fair', 0.42, 0.5)}
        hide={inB('fair', 0.9, 1)}
        tone='pink'
      >
        Danke für mein Stärkenprofil!
      </Bubble>
    </div>
  )
}

// ---------- Vergleich am Ende ----------

const COMPARE = [
  ['Lebenslauf', 'Der Mensch dahinter'],
  ['Viel Handarbeit', 'Vorauswahl mit Aivy'],
  ['Fehlbesetzung', 'Passung, die bleibt'],
  ['Absage ohne Feedback', 'Stärkenprofil für alle'],
  ['Talent verloren', 'Kreuzmatching']
]

// ---------- Bühne ----------

export default function HumanStory() {
  const stage = useRef(null)
  const world = useRef(null)
  const last = useRef(0)
  const [beat, setBeat] = useState(0)

  const place = (l) => {
    const host = stage.current
    if (!host || !world.current) return
    const narrow = host.clientWidth < 700
    const fit =
      Math.min(host.clientWidth / W, host.clientHeight / H) *
      (narrow ? 1.9 : 1.08)
    const cam = camera(l)
    const s = fit * cam.z
    const cx = host.clientWidth * (0.5 + (narrow ? 0 : cam.ox))
    const cy = host.clientHeight * (narrow ? 0.4 : 0.48)
    world.current.style.transform = `translate(${cx - cam.x * s}px, ${cy - cam.y * s}px) scale(${s})`
  }

  useFrame((l) => {
    last.current = l
    place(l)
    const i = BEATS.findIndex((b) => l >= b.at[0] && l < b.at[1])
    setBeat(i === -1 ? BEATS.length - 1 : i)
  })

  useEffect(() => {
    const ro = new ResizeObserver(() => place(last.current))
    if (stage.current) ro.observe(stage.current)
    return () => ro.disconnect()
  }, [])

  const jump = (i) => {
    const section = stage.current?.closest('.off-section')
    if (!section) return
    const top = section.getBoundingClientRect().top + window.scrollY
    const [s, e] = BEATS[i].at
    const target =
      top + (s + (e - s) * 0.5) * (section.offsetHeight - window.innerHeight)
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      top: i === 0 ? top : target,
      behavior: reduce ? 'auto' : 'smooth'
    })
  }

  const toTop = () => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' })
  }

  const maxPath = [
    { at: atB('ohne', 0.42), ...SPOTS.door },
    { at: atB('ohne', 0.48), x: 22.2, y: 5.6 },
    { at: atB('ohne', 0.54), x: 20.4, y: 18.4 },
    { at: atB('ohne', 0.6), ...SPOTS.csSeat },
    { at: atB('ohne', 0.82), ...SPOTS.csSeat },
    { at: atB('ohne', 0.86), x: 20.4, y: 18.4 },
    { at: atB('ohne', 0.92), x: 22.2, y: 5.6 },
    { at: atB('ohne', 0.97), ...SPOTS.door }
  ]

  return (
    <div ref={stage} className='off-stage'>
      <div
        ref={world}
        className='off-world'
        style={{
          width: W,
          height: H,
          ...pv('ga', inB('ohne', 0, 0.05)),
          ...pv('gb', inB('rewind', 0.35, 0.85)),
          filter: 'grayscale(calc(var(--ga) * (1 - var(--gb)) * 0.9))'
        }}
      >
        <svg
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          className='off-svg'
          aria-hidden='true'
        >
          <OfficeWorld4 />

          <Flight
            items={PAST_LETTERS}
            render={(it) => (
              <g transform={`rotate(${it.rot})`}>
                <Envelope />
              </g>
            )}
          />
          <Flight items={INVITES} render={() => <Envelope />} />
          <Flight items={ANALYSES} render={renderShape} />
          <Flight
            items={LETTERS}
            render={(it) => (
              <g transform={`rotate(${it.rot})`}>
                <Envelope />
              </g>
            )}
          />

          {/* Mara bleibt am Schreibtisch */}
          <g
            transform={`translate(${iso(SPOTS.mara.x, SPOTS.mara.y).x} ${iso(SPOTS.mara.x, SPOTS.mara.y).y})`}
          >
            <Person
              body='blue'
              skin='#E0AC69'
              hair='#5A3825'
              happy={inB('shortlist', 0.5, 0.6)}
              sad={[...inB('ohne', 0.86, 0.92), ...inB('rewind', 0.3, 0.6)]}
              sweat={[...inB('ohne', 0.16, 0.24), ...inB('ohne', 0.4, 0.46)]}
            />
            <Heart
              x={-12}
              y={-80}
              at={inB('einstellung', 0.36, 0.44)}
              out={inB('einstellung', 0.56, 0.62)}
            />
            <Heart x={0} y={-78} at={inB('finale', 0.1, 0.25)} />
          </g>

          {/* Max, die Fehlbesetzung aus der Rückblende */}
          <g
            style={anim({
              at: inB('ohne', 0.4, 0.43),
              out: inB('ohne', 0.95, 0.99),
              y: 0,
              oy: 0
            })}
          >
            <Walker path={maxPath}>
              <Person
                body='#9AA5B8'
                skin='#F2C9A5'
                hair='#5A3825'
                happy={[2, 3]}
                sad={[...inB('ohne', 0.66, 0.72), 2, 3]}
              />
            </Walker>
          </g>

          {TALENTS.map((t, i) => {
            const path = talentPath(t)
            const leaves = LEAVERS.includes(t)
            return (
              <Walker key={t.id} path={path}>
                <Talent
                  talent={t}
                  spawn={spawnAt(i)}
                  exitAt={leaves ? exitArrival(path) : undefined}
                />
                {t.id === 'lena' && (
                  <>
                    <Heart
                      x={12}
                      y={-104}
                      at={inB('einstellung', 0.38, 0.46)}
                      out={inB('einstellung', 0.56, 0.62)}
                    />
                    <Heart x={0} y={-104} at={inB('finale', 0.1, 0.25)} />
                  </>
                )}
                {t.id === 'jonas' && (
                  <Heart x={0} y={-104} at={inB('finale', 0.1, 0.25)} />
                )}
              </Walker>
            )
          })}

          {['csLead', 'csMate', 'csNew'].map((id, i) => {
            const s = iso(STAFF_BY_ID[id].x, STAFF_BY_ID[id].y)
            return (
              <Heart
                key={id}
                x={s.x}
                y={s.y - 80}
                at={inB('einstellung', 0.9 + i * 0.03, 0.96 + i * 0.03)}
                out={inB('fair', 0.2, 0.3)}
              />
            )
          })}
        </svg>

        <Overlays />
      </div>

      {/* Bildschirm-Ebene */}
      <nav className='off-nav' aria-label='Kapitel'>
        {BEATS.map((b, i) => (
          <button
            key={b.id}
            type='button'
            className={clsx('off-nav__dot', i === beat && 'is-active')}
            onClick={() => jump(i)}
            aria-label={b.label}
            title={b.label}
          />
        ))}
        <span className='off-nav__label'>{BEATS[beat].label}</span>
      </nav>

      <div
        className='off-intro'
        style={anim({ at: [-1, 0], out: inB('intro', 0.6, 1), oy: -40 })}
      >
        <div className='off-eyebrow'>Recruiting mit Aivy</div>
        <h2 className='off-title'>
          Hinter jedem Lebenslauf steckt ein Mensch.
        </h2>
        <p>Eine Geschichte in zwei Anläufen. Erst ohne Aivy, dann mit.</p>
        <div className='off-cue'>
          <ArrowDownIcon width={16} height={16} /> Scroll los
        </div>
      </div>

      <div
        className='hum-tag'
        style={anim({
          at: inB('ohne', 0.02, 0.08),
          out: inB('rewind', 0.3, 0.5),
          y: -10,
          oy: -10
        })}
      >
        Rückblende · ohne Aivy
      </div>

      <div
        className='hum-cost'
        style={anim({
          at: inB('ohne', 0.9, 0.96),
          out: inB('rewind', 0.12, 0.24),
          y: -20,
          oy: -20
        })}
      >
        <span>Zeit verloren</span>
        <span>Budget verloren</span>
        <span>Team frustriert</span>
      </div>

      <div
        className='hum-rewind'
        style={anim({
          at: inB('rewind', 0.08, 0.24),
          out: inB('rewind', 0.82, 0.98),
          s: 0.8,
          y: 20
        })}
      >
        <span className='hum-rewind__icon'>
          <BackwardIcon width={34} height={34} />
        </span>
        <strong>Nochmal. Diesmal mit Aivy.</strong>
      </div>

      <div
        className='hum-headline'
        style={anim({
          at: inB('menschen', 0.3, 0.42),
          out: inB('menschen', 0.9, 1),
          y: 30,
          oy: -20
        })}
      >
        Aus Lebensläufen werden Menschen.
      </div>

      <Panel
        at={inB('regeln', 0.06, 0.2)}
        out={inB('regeln', 0.9, 1)}
        className='off-doc hum-rules'
      >
        <div className='off-doc__eyebrow'>Regelbasiert</div>
        <div className='off-doc__title'>Gleiche Regeln für alle.</div>
        <p className='off-doc__lead'>
          Aivy gleicht jede Person nach denselben festen Regeln mit dem
          Anforderungsprofil ab. Ohne Blackbox, nachvollziehbar bis auf jede
          Dimension.
        </p>
        <div className='hum-rules__beam'>
          <Beam ideal={[56, 82]} trans={[44, 92]} value={70} />
          <div className='off-legend'>
            <span>
              <i style={{ background: 'rgb(167 247 231)' }} /> Idealbereich
            </span>
            <span>
              <i style={{ background: 'rgb(253 218 148)' }} /> Übergangsbereich
            </span>
          </div>
        </div>
        <div className='hum-rules__rings'>
          <span>
            <b style={{ borderColor: COLORS.green }} /> passt zu Customer
            Success
          </span>
          <span>
            <b style={{ borderColor: COLORS.blue }} /> passt zum Vertrieb
          </span>
        </div>
        <div className='hum-rules__chips'>
          <span className='off-chip off-chip--blue'>
            <ScaleIcon width={12} height={12} /> Im Kern ohne KI
          </span>
          <span className='off-chip'>AI-Act-ready</span>
          <span className='off-chip'>
            <SparklesIcon width={12} height={12} /> KI nur zum Erklären
          </span>
        </div>
      </Panel>

      {BEATS.filter((b) => b.text).map((b) => {
        const i = BEATS.indexOf(b)
        const len = b.at[1] - b.at[0]
        return (
          <div
            key={b.id}
            className='off-caption'
            style={anim({
              at: [b.at[0] + len * 0.04, b.at[0] + len * 0.14],
              out: [b.at[1] - len * 0.08, b.at[1]],
              y: 24,
              oy: -16
            })}
          >
            <div className='off-eyebrow'>
              {i} · {b.label}
            </div>
            <p>{b.text}</p>
          </div>
        )
      })}

      <div
        className='hum-compare'
        style={anim({ at: inB('finale', 0.3, 0.5), y: 30 })}
      >
        <h2 className='off-title'>
          Alle happy. Auch die, die nicht genommen wurden.
        </h2>
        <div className='hum-compare__grid'>
          <span className='hum-compare__head'>Ohne Aivy</span>
          <span />
          <span className='hum-compare__head hum-compare__head--on'>
            Mit Aivy
          </span>
          {COMPARE.map(([a, b], i) => (
            <div
              key={a}
              className='hum-compare__row'
              style={anim({
                at: inB('finale', 0.4 + i * 0.06, 0.5 + i * 0.06),
                y: 12
              })}
            >
              <span className='hum-compare__old'>{a}</span>
              <span className='hum-compare__arrow'>→</span>
              <span className='hum-compare__new'>{b}</span>
            </div>
          ))}
        </div>
        <div className='off-outro__actions'>
          <Link to='/career/new' className='off-cta'>
            Stelle anlegen
          </Link>
          <button
            type='button'
            onClick={toTop}
            className='off-cta off-cta--ghost'
          >
            Nochmal von vorn
          </button>
        </div>
      </div>
    </div>
  )
}
